import {
  Button,
  Copy,
  Flex,
  Square,
  Squish,
  Stack,
  SvgMagnifyingGlass,
} from '@grubhubprod/gh-cookbook-react/dist';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form/dist/index.ie11';
import { Section } from '@grubhubprod/gh-cookbook-react';
import { Input } from 'src/components/Input/Input';
import { Modal } from 'src/components/Modal/Modal';
import Table from 'src/components/Table/Table';
import { useDebouncedValue } from 'src/utilities/useDebouncedValue';
import { useScreenSizes } from 'src/utilities/useScreenSizes';
import { SearchStatus, useGroupSearch } from 'src/utilities/useSearchHooks';
import { pushErrorFromAPI } from 'src/utilities/notifications';
import { DeprecatedCookbookCheckboxWrapper } from 'src/components/DeprecatedCookbookCheckboxWrapper/DeprecatedCookbookCheckboxWrapper';
import { AddGroupsToLocTableHeader } from 'src/components/selfService/features/LineOfCredit/details/Groups/AddGroupsToLocTableHeader';
import { AddGroupsToLocLoadMoreButton } from 'src/components/selfService/features/LineOfCredit/details/Groups/AddGroupsToLocLoadMoreButton';
import { AddGroupsToLocItem } from 'src/components/selfService/features/LineOfCredit/details/Groups/AddGroupsToLocItem';
import { useSendAnalytics } from 'src/components/AnalyticsProvider/useSendAnalytics';
import { RouterLink } from 'src/components/RouterLink/RouterLink';

const FIXED_BODY_HEIGHT = '300px';
const DEBOUNCE_SEARCH_TIMING = 200;

export interface IAddGroupsToLocModalProps {
  corpId: string;
  onClose: () => void;
  onAdd: (selectedGroupIds: string[]) => void;
  existingGroupsMap: Record<string, boolean>;
  isLoading: boolean;
}

type FormFields = {
  selectedGroupIds: string[];
  selectAll: boolean;
  searchTerm: string;
};

export default function AddGroupsToLocModal({
  corpId,
  onClose,
  onAdd,
  existingGroupsMap,
  isLoading: isLoadingProp,
}: IAddGroupsToLocModalProps): JSX.Element {
  const { isPhone, isDesktop } = useScreenSizes();

  const { sendAnalytics } = useSendAnalytics({
    eventcategory: 'self-service management tool_loc',
    eventaction: 'groups tab_cta',
    eventlabel: 'hyperlink in Assign Groups modal',
  });

  const {
    resultsList: groups,
    totalResults,
    searchStatus: groupsSearchStatus,
    hasMoreResults,
    loadMoreNumber,
    doSearch: getGroups,
    error,
    hasNoResultsWithQuery,
    hasNoResultsWithoutQuery,
  } = useGroupSearch(corpId, true);

  const { register, setValue, watch, control } = useForm<FormFields>({
    mode: 'onChange',
    defaultValues: { selectedGroupIds: [], selectAll: false },
  });

  const debouncedSearchTerm = useDebouncedValue(
    watch('searchTerm', ''),
    DEBOUNCE_SEARCH_TIMING
  );
  const selectedGroupIds = watch('selectedGroupIds');
  watch('selectAll', false);

  const isSelectionActive = !!selectedGroupIds.length;

  useEffect(() => {
    if (groupsSearchStatus === SearchStatus.ERROR) {
      pushErrorFromAPI(error, {
        fallbackMessage:
          'Sorry, there was an issue retrieving the groups for this account',
      });
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsSearchStatus]);

  const doSearch = useCallback(
    (isLoadMore = false) => {
      if (debouncedSearchTerm) {
        resetSelections();
        getGroups({ query: debouncedSearchTerm }, isLoadMore);
      } else {
        getGroups({}, isLoadMore);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [debouncedSearchTerm, getGroups]
  );

  useEffect(() => {
    doSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchTerm]);

  const selectableGroupIds = useMemo(
    () =>
      groups.reduce((acc, { id, diner_count }) => {
        if (!existingGroupsMap[id] && diner_count) {
          acc.push(id);
        }
        return acc;
      }, []),
    [groups, existingGroupsMap]
  );

  const indeterminate =
    selectedGroupIds.length > 0 &&
    selectableGroupIds.length !== selectedGroupIds.length;

  const selectAll = watch('selectAll');

  useEffect(() => {
    if (selectAll) {
      if (!indeterminate) {
        setValue('selectedGroupIds', selectableGroupIds);
      }
    } else {
      setValue('selectedGroupIds', []);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectAll]);

  function loadMore() {
    doSearch(true);
    resetSelections();
  }

  function resetSelections() {
    setValue('selectAll', false);
  }

  useEffect(() => {
    setValue('selectAll', !!selectedGroupIds.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedGroupIds.length]);

  const isLoading =
    groupsSearchStatus === SearchStatus.LOADING || isLoadingProp;

  const addGroupsPageUrl = `/corporate/management/${corpId}/people?tab=groups`;

  const handleAddGroupsLinkClicked = () => {
    sendAnalytics();
  };

  return (
    <Modal size="sm">
      <Square position="relative">
        <Table
          hideHeader={false}
          isLoading={isLoading}
          bodyMinHeight={FIXED_BODY_HEIGHT}
          bodyMaxHeight={isDesktop ? FIXED_BODY_HEIGHT : 'auto'}
          headerInsets={isDesktop ? 'lg' : 'md'}
          header={() => (
            <AddGroupsToLocTableHeader
              onCloseClick={onClose}
              searchInputElement={
                <Input
                  key="searchTerm"
                  ariaLabel="Search for group name"
                  placeholder="Search for group name"
                  name="searchTerm"
                  id="searchTerm"
                  prefixIcon={
                    <Copy variant="body" color="secondaryText">
                      <SvgMagnifyingGlass size="16px" />
                    </Copy>
                  }
                  ref={register}
                />
              }
              selectAllCheckboxElement={
                <DeprecatedCookbookCheckboxWrapper
                  control={control}
                  name="selectAll"
                  id="selectAll"
                  aria-label="select all groups"
                  indeterminate={indeterminate}
                />
              }
              numberOfSelectedGroups={selectedGroupIds.length}
              numberOfGroupsInView={groups.length}
              numberOfTotalGroups={totalResults}
            />
          )}
          body={() => (
            <>
              {groups.length > 0 && (
                <>
                  <Stack as="ul" className="removeListStyles">
                    {groups.map((group) => (
                      <AddGroupsToLocItem
                        key={group.id}
                        corpId={corpId}
                        group={group}
                        isAssigned={existingGroupsMap[group.id]}
                        control={control}
                      />
                    ))}

                    {hasMoreResults && (
                      <AddGroupsToLocLoadMoreButton
                        itemsToLoadMore={loadMoreNumber}
                        onClick={loadMore}
                      />
                    )}
                  </Stack>
                  <Flex alignItems="center" justifyContent="center">
                    <Square inset="lg" style={{ textAlign: 'center' }}>
                      <Copy variant="body" color="secondaryText">
                        Don't see your group here? Add them to your company
                        <br /> on the{' '}
                        <RouterLink
                          href={addGroupsPageUrl}
                          preNavigationFunction={handleAddGroupsLinkClicked}
                        >
                          People page
                        </RouterLink>
                      </Copy>
                    </Square>
                  </Flex>
                </>
              )}

              {hasNoResultsWithoutQuery && (
                <Section
                  inset="md"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Copy variant="body" textAlign="center">
                    There are no groups in your account. You can create groups
                    on the{' '}
                    <RouterLink
                      href={addGroupsPageUrl}
                      preNavigationFunction={handleAddGroupsLinkClicked}
                    >
                      People page
                    </RouterLink>
                    .
                  </Copy>
                </Section>
              )}

              {hasNoResultsWithQuery && (
                <Section
                  inset="md"
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                >
                  <Copy variant="body" textAlign="center">
                    We couldn't find the group you were looking for <br />
                    Give it another go or add to your account on{' '}
                    <RouterLink
                      href={addGroupsPageUrl}
                      preNavigationFunction={handleAddGroupsLinkClicked}
                    >
                      people page
                    </RouterLink>
                  </Copy>
                </Section>
              )}
            </>
          )}
          isSignupOrSetupTable={false}
          justifyFooter="space-between"
          footerMinHeight="auto"
          footerBg="untintedBackground"
          showFooter
          footer={() => (
            <Squish inset="lg" width="100%">
              <Flex
                alignItems="center"
                justifyContent="space-between"
                flexDirection={isPhone ? 'column' : 'row'}
              >
                <Stack
                  y={isPhone ? 'md' : 'none'}
                  x={!isPhone ? 'md' : 'none'}
                  width="100%"
                >
                  <Button
                    block
                    variant="primary"
                    buttonSize="medium"
                    disabled={!isSelectionActive}
                    onClick={() => {
                      onAdd(selectedGroupIds);
                    }}
                  >
                    Assign group ({selectedGroupIds.length})
                  </Button>
                </Stack>

                <Button
                  block
                  variant="secondary"
                  buttonSize="medium"
                  onClick={onClose}
                >
                  Cancel
                </Button>
              </Flex>
            </Squish>
          )}
        />
      </Square>
    </Modal>
  );
}
